import OtpInput from 'react-otp-input';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Alert } from '../../services/notiflix.service';
import AuthService from '../../services/auth.service';
import AccountSettingsService from '../../services/account-settings.service';
import {
  CircleRunningIcon,
  CloseButtonSvgIcon,
  CloseEye,
  CopySignIcon,
  LessThanSignIcon,
  OpenEye,
} from './IconProvider';
import ImageLoader from './ImageLoader';
import { useTranslation } from 'react-i18next';
const TwoFactorAuth = ({ profileData, TwoFa, getProfileDetails }) => {
  const [isLoader, setLoader] = useState(false);
  const [isErrorMsg, setErrorMsg] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const { t } = useTranslation(['auth', 'common']);
  let validationShape = {
    verifyPassword: Yup.string()
      .required(t('password_required'))
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\])[A-Za-z\d@$!%*#?&-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\]{8,}$/,
        t('password_requirements'),
      ),
  };
  const [twoFactorData, setTwoFactorData] = useState({
    secret: '',
    qrCode: '',
  });
  const [twoFactorCode, setTwoFactorCode] = useState(0);
  const [is2FAsteps, set2FAsteps] = useState(1);
  const generateTwoFactorSecret = async (formData) => {
    try {
      let resp = await AccountSettingsService.generateTwoFactorSecret(formData);
      if (resp.status === 'failure') {
        Alert.error(resp.message);
        return resp;
      }

      setTwoFactorData({
        secret: resp.data.two_factor_secret,
        qrCode: resp.data.qrCode,
      });
      return resp;
    } catch (error) {
      return error;
    }
  };
  const _validationSchema = Yup.object().shape(validationShape);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(_validationSchema),
  });
  const initiatePasswordConfirmation = async (_formInput) => {
    setLoader(true);
    try {
      let formData = {
        password: _formInput.verifyPassword,
      };
      reset();

      let response = await AuthService.validatePassword(formData);

      if (response.status === 'failure') {
        setLoader(false);
        if (response.message === 'Invalid password.') {
          setErrorMsg(response.message);
        } else {
          Alert.error(response.message);
        }

        return false;
      }

      if (profileData.is_two_factor_enabled === 0) {
        let resp = await generateTwoFactorSecret({ regenerate: true });

        if (resp.status === 'failure') {
          setLoader(false);
          return false;
        }

        set2FAsteps(2);
      } else {
        let resp = await update2FAStatus();
        if (resp.status === 'failure') {
          setLoader(false);
          Alert.error(t('deactivation_error'));
          return false;
        }
        TwoFa(false);
        await getProfileDetails();

        Alert.success(t('two_factor_auth_disabled_successfully'));
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
    }
  };
  const update2FAStatus = async () => {
    try {
      let resp = await AccountSettingsService.update2FAStatus();
      return resp;
    } catch (error) {
      return error;
    }
  };

  const handle2FACodeChange = (_otp) => {
    setErrorMsg(null);
    setTwoFactorCode(_otp);
  };

  const verify2FACode = async () => {
    if (twoFactorCode === null || twoFactorCode?.length < 6) {
      setErrorMsg(t('two_factor_auth_code_length'));
      return false;
    }
    setLoader(true);
    try {
      let formData = {
        code: twoFactorCode,
        is_two_factor_enabled: 1,
      };

      let resp = await AuthService.verifyTwoFA(formData);

      if (resp.status === 'failure') {
        if (
          resp.message !==
          'Something went wrong while verifying two factor authentication code, please try again later.'
        ) {
          setErrorMsg(resp.message);
        } else {
          Alert.error(resp.message);
        }
        setLoader(false);
        return false;
      }

      await getProfileDetails();
      TwoFa(false);
      setLoader(false);
      Alert.success(t('two_factor_auth_enabled_successfully'));
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
    }
  };

  const copyWalletAddress = () => {
    Alert.success(t('secret_key_copied'));
  };

  return (
    <>
      <div>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-full mx-3 my-4 max-w-md rounded-lg overflow-hidden">
            {/*content*/}

            <div className="border-0  shadow-lg relative flex flex-col w-full bg-white dark:bg-slate-800  dark:text-slate-200 outline-none focus:outline-none">
              {/*header*/}
              {is2FAsteps === 1 && (
                <>
                  <div className="flex justify-between items-center py-3 px-6  border-b dark:border-gray-500">
                    <h3 className="text-2xl">{t('verify_identity')}</h3>

                    <button
                      onClick={() => TwoFa(false)}
                      className="p-1 ml-auto bg-transparent border-0 text-black text-3xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-3 z-10 flex justify-center items-center"
                    >
                      <span className="bg-transparent text-black w-10 dark:bg-slate-400 dark:rounded-md block outline-none focus:outline-none">
                        <CloseButtonSvgIcon />
                      </span>
                    </button>
                  </div>
                  <form onSubmit={handleSubmit(initiatePasswordConfirmation)}>
                    <div className="relative p-6 pt-3 flex-auto mb-28">
                      <div className="relative mb-1">
                        <p className="text-sm mb-4">
                          {t('in_order_to_turn')}{' '}
                          {profileData.is_two_factor_enabled === 0
                            ? 'on'
                            : 'off'}{' '}
                          {t('two_factor_authentication_instruction')}
                        </p>
                        <div className="relative">
                          <input
                            placeholder={t('password')}
                            name="verifyPassword"
                            type={passwordShown ? 'text' : 'password'}
                            onClick={() => setErrorMsg(null)}
                            {...register('verifyPassword', { required: true })}
                            className="mb-1"
                          />
                          <div
                            onClick={() =>
                              setPasswordShown(passwordShown ? false : true)
                            }
                          >
                            <div className="absolute right-0 top-1 w-10 h-10 flex justify-center items-center">
                              {passwordShown ? <CloseEye /> : <OpenEye />}
                            </div>
                          </div>
                        </div>
                      </div>
                      {errors.verifyPassword && (
                        <div className="common-error-text">
                          {errors.verifyPassword?.message}
                        </div>
                      )}
                      {isErrorMsg !== null && !errors.verifyPassword && (
                        <div className="common-error-text">{isErrorMsg}</div>
                      )}
                    </div>
                    <div className=" pt-2 pb-4 px-6 text-center flex flex-col gap-2 justify-between">
                      <button
                        className={`twofactor-style-button
                          ${
                            isLoader
                              ? ' bg-gray-500 border-gray-500'
                              : 'bg-GreenColor border-GreenColor text-center active:bg-[#92C24B] w-full text-brandBlue'
                          }`}
                        type="submit"
                        disabled={isLoader ? true : false}
                      >
                        {isLoader ? (
                          <CircleRunningIcon />
                        ) : (
                          t('confirm_password')
                        )}

                        {/* Confirm Password */}
                      </button>
                    </div>
                  </form>
                </>
              )}
              {is2FAsteps === 2 && (
                <>
                  <div className="flex justify-between items-center py-5 px-6  border-b dark:border-gray-500">
                    <h3 className="text-xl">
                      {t('configure_authentication_app')}
                    </h3>

                    <button
                      onClick={() => TwoFa(false)}
                      className="p-1 ml-auto bg-transparent border-0 text-black text-3xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-3 z-10 flex justify-center items-center"
                    >
                      <span className="bg-transparent text-black w-10 dark:bg-slate-400 dark:rounded-md block outline-none focus:outline-none">
                        <CloseButtonSvgIcon />
                      </span>
                    </button>
                  </div>

                  <div className="relative p-6 pt-3 flex-auto">
                    <p>{t('download_authenticator_app')}</p>
                    <p>{t('add_new_account')}</p>
                    <p>{t('scan_qr_code')}</p>
                    <div className="flex items-center justify-center pt-6 pb-5">
                      <div className="w-40 p-4 rounded-2xl border border-gray-100">
                        <ImageLoader
                          src={twoFactorData.qrCode}
                          alt=""
                          className="w-36 mx-auto"
                        />
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-600 px-4 py-3 rounded-md flex items-center border border-gray-200 justify-between">
                      <p className="truncate block mr-4">
                        {twoFactorData.secret}
                      </p>
                      <CopyToClipboard
                        text={twoFactorData.secret}
                        onCopy={copyWalletAddress}
                      >
                        <button className="w-10 h-10 rounded-lg p-1 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                          <CopySignIcon />
                        </button>
                      </CopyToClipboard>
                    </div>
                  </div>
                  <div className=" pt-2 pb-4 px-6 text-center flex flex-col gap-2 justify-between">
                    <button
                      className="bg-GreenColor active:bg-[#92C24B] w-full text-brandBlue border border-GreenColor twofactor-style-button"
                      onClick={() => set2FAsteps(3)}
                    >
                      {t('common:continue')}
                    </button>
                  </div>
                </>
              )}
              {is2FAsteps === 3 && (
                <>
                  <div className="flex justify-between items-center py-3 pr-6 pl-2 border-b">
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          set2FAsteps(2);
                          setErrorMsg(null);
                          setTwoFactorCode(null);
                        }}
                        className="w-10 h-10 flex items-center justify-center p-1 mr-3 bg-gray-50 rounded-full"
                      >
                        <LessThanSignIcon />
                      </button>
                      <h3 className="text-2xl">
                        {t('common:authenticate_account')}
                      </h3>
                    </div>
                  </div>

                  <div className="relative p-6 pt-3 flex-auto">
                    <p className="mb-5 text-sm text-[#85919F] dark:text-slate-300 text-left">
                      {t('common:confirm_account_security')}
                    </p>

                    <div className="flex items-center justify-center w-full py-6">
                      <OtpInput
                        containerStyle="w-full justify-center"
                        inputStyle="min-w-[44px] h-11 p-3"
                        value={twoFactorCode}
                        onChange={handle2FACodeChange}
                        numInputs={6}
                        renderSeparator={<span>-</span>}
                        renderInput={(props) => <input {...props} />}
                      />
                    </div>
                    {isErrorMsg !== null && (
                      <div className="text-red-500 text-sm text-center">
                        {isErrorMsg}
                      </div>
                    )}
                  </div>

                  <div className=" pt-2 pb-4 px-6 text-center flex flex-col gap-2 justify-between">
                    <button
                      className={`active:bg-[#92C24B] w-full text-brandBlue twofactor-style-button
                        ${
                          isLoader
                            ? 'bg-gray-500 border-gray-500'
                            : 'bg-GreenColor border-GreenColor'
                        }`}
                      onClick={() => verify2FACode()}
                      disabled={isLoader ? true : false}
                    >
                      {isLoader ? <CircleRunningIcon /> : t('verify')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
      </div>
    </>
  );
};

export default TwoFactorAuth;
